import { ColorDefinition } from "@/types/colors";
import i18n from "@i18n/i18n";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import _ from "lodash";
import { observer } from "mobx-react";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import Chart, { GoogleChartWrapper, GoogleChartWrapperChartType, ReactGoogleChartEvent } from "react-google-charts";
import useState from "react-usestateref";

export interface BarChartProps {
  id: string;
  width?: number;
  height?: number;
  data: any;
  colors?: ColorDefinition[];
  chartOptions?: any;
  slantedText: boolean;
  chartType?: GoogleChartWrapperChartType;
  onSelect: (rowIndex?: number, legend?: string | undefined) => void;
}

export type BarChartApi = {
  setSelection: ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => void;
};

const BarChart = observer(
  forwardRef((props: BarChartProps, ref: any) => {
    console.log("BarChart: ", props);
    const theme = useTheme();
    const [key, setKey] = useState(false);
    const [chartWrapper, setChartWrapper] = useState<GoogleChartWrapper | null>(null);
    const [selectionModel, setSelectionModel] = useState<number[] | null>(null);
    const [prevSelectionModel, setPrevSelectionModel, prevSelectionModelRef] = useState<any>();

    useImperativeHandle(ref, () => ({
      setSelection: ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => {
        updateChartSelection({ rowIndex, legends });
      },
    }));

    useEffect(() => {
      if (chartWrapper && selectionModel) {
        //console.log("Props:", props);
        const newSelections: { row?: any; column?: any }[] = [];
        if (props.chartType === "BarChart") {
          newSelections.push({ row: selectionModel[0], column: 1 });
        } else {
          for (let i = 0; i < props.data.length - 1; i++) {
            selectionModel.forEach((column) => {
              newSelections.push({ row: i, column });
            });
          }
        }
        //console.log("Proposed Selections: ", selections);
        const chart: any = chartWrapper.getChart(); // type does not expose setSelection method so cast to any
        const selections = chart.getSelection();
        if (!_.isEqual(selections, newSelections)) {
          console.log("setSelection: ", newSelections);
          chart.setSelection(newSelections);
        }
      }
    }, [chartWrapper, selectionModel]);

    const handleOnSelect = (rowIndex: number, legend: string | undefined) => {
      updateChartSelection({ legends: legend });
      props.onSelect(rowIndex, legend);
    };

    const updateChartSelection = ({ rowIndex, legends }: { rowIndex?: number; legends?: string | string[] | undefined }) => {
      console.log("updateChartSelection: ", rowIndex, legends);
      if (rowIndex !== undefined) {
        setSelectionModel([rowIndex + 1]); // Need to add 1 to account for the header row
      } else {
        if (!Array.isArray(legends)) {
          legends = legends ? [legends] : [];
        }
        const columns = legends.map((legend) => props.data[0]?.indexOf(legend)) || [];
        setSelectionModel(columns);
      }
    };

    let chartOptions = {
      titleTextStyle: {
        color: theme.palette.text.primary,
      },
      annotations: {
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      hAxis: {
        title: i18n.t("barchart.title.hAxis"),
        minValue: 0,
        slantedText: props.slantedText,
        textStyle: {
          color: theme.palette.text.primary,
        },
        titleTextStyle: {
          color: theme.palette.text.primary,
        },
      },
      vAxis: {
        title: i18n.t("barchart.title.vAxis"),
        textStyle: {
          color: theme.palette.text.primary,
        },
        titleTextStyle: {
          color: theme.palette.text.primary,
        },
      },
      colors: [] as string[],
      legend: {
        position: "none", // 'right'
        maxLines: 10,
        textStyle: {
          fontSize: 12,
          color: theme.palette.text.primary,
        },
      },
      isStacked: true,
      chartArea: {
        top: 60,
        backgroundColor: {
          fill: theme.palette.background.default,
        },
      },
      backgroundColor: {
        fill: theme.palette.background.default,
      },
    };

    const selectCallback = ({ chartWrapper }: { chartWrapper: GoogleChartWrapper | null }) => {
      setChartWrapper(chartWrapper);
      if (!chartWrapper) {
        return;
      }

      const chart: any = chartWrapper.getChart();
      let selection = chart.getSelection();
      console.log("getSelection: ", selection);
      if (selection.at(0)) {
        setPrevSelectionModel(selection);
      } else if (prevSelectionModelRef.current) {
        chart.setSelection(prevSelectionModelRef.current);
        selection = prevSelectionModelRef.current;
      }

      if (!_.isNil(selection.at(0))) {
        const dataTable = chartWrapper.getDataTable();

        // NOTE: row is the index of the data row, which is represented as a column in the chart
        // so this becomes colIndex in the callback
        const row = selection.at(0)?.row;
        const column = selection.at(0)?.column - 1; // subtract 1 to account for the header row
        const legend = dataTable?.getColumnLabel(selection.at(0)?.column);

        const rowIndex = props.chartType === "BarChart" ? row : column;
        handleOnSelect(rowIndex, legend);
      } else {
        // reset selection
        //props.onSelect();
      }
    };

    const chartEvents: ReactGoogleChartEvent[] = [
      {
        eventName: "select",
        callback: selectCallback,
      },
      {
        eventName: "ready",
        callback: ({ chartWrapper }: { chartWrapper: GoogleChartWrapper | null }) => {
          setChartWrapper(chartWrapper);
        },
      },
    ];

    chartOptions = _.merge(chartOptions, props.chartOptions);
    chartOptions = {
      ...chartOptions,
    };

    if (props.colors) {
      const colors = props.colors.map((c) => c.hex).slice(0, props.data[0]?.length - 1); // makes a shallow copy of the array
      //console.log("colors: ", colors);
      chartOptions.colors = colors;
    }

    useEffect(() => {
      setKey((prevKey) => !prevKey);
    }, [props?.width, props?.height]);

    return (
      <Box
        sx={{ position: "absolute", top: 0, left: 0, right: props?.width, bottom: props?.height, '& [stroke-width="1"]': { strokeWidth: 2, stroke: theme.palette.text.primary } }}
      >
        <Chart
          key={`${props.id}-${key}`}
          width={props.width}
          height={props.height}
          chartType={props.chartType || "ColumnChart"}
          loader={<div>{i18n.t("barchart.title.chart-progress")}</div>}
          data={props.data}
          options={chartOptions as any}
          chartEvents={chartEvents}
        />
      </Box>
    );
  }),
);

export default BarChart;
